import React, { useMemo } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const AppointmentCalendar = ({
  month,
  onMonthChange,
  days = [],
  events = [],
  selectedDate,
  onSelectDate,
}) => {
  const daysByDate = useMemo(() => {
    const map = {};
    days.forEach((day) => {
      map[day.date] = day;
    });
    return map;
  }, [days]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((event) => {
      if (!map[event.date]) map[event.date] = [];
      map[event.date].push(event);
    });
    return map;
  }, [events]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    return eachDayOfInterval({
      start: startOfWeek(monthStart, { weekStartsOn: 1 }),
      end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
    });
  }, [month]);

  const monthLabel = format(month, 'MMMM yyyy', { locale: fr });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <button
          type="button"
          onClick={() => onMonthChange(subMonths(month, 1))}
          className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
          aria-label="Mois précédent"
        >
          <FiChevronLeft />
        </button>
        <h2 className="text-lg font-semibold text-gray-900 capitalize">{monthLabel}</h2>
        <button
          type="button"
          onClick={() => onMonthChange(addMonths(month, 1))}
          className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
          aria-label="Mois suivant"
        >
          <FiChevronRight />
        </button>
      </div>

      <div className="grid grid-cols-7 border-b border-gray-100">
        {WEEKDAYS.map((day) => (
          <div key={day} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {calendarDays.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const dayMeta = daysByDate[dateStr];
          const dayEvents = eventsByDate[dateStr] || [];
          const inMonth = isSameMonth(day, month);
          const selected = selectedDate && isSameDay(day, selectedDate);
          const hasConfirmed = dayEvents.some((e) => e.status === 'confirmed');
          const hasPending = dayEvents.some((e) => e.status === 'pending');
          const availableSlots = dayMeta?.slots?.filter((s) => s.status === 'available').length ?? 0;

          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => onSelectDate(day)}
              className={`min-h-[88px] p-2 border-b border-r border-gray-100 text-left transition ${
                inMonth ? 'bg-white hover:bg-primary-50' : 'bg-gray-50 text-gray-400'
              } ${selected ? 'ring-2 ring-inset ring-primary-500 bg-primary-50' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-medium ${
                    isToday(day) ? 'bg-primary-600 text-white rounded-full w-7 h-7 flex items-center justify-center' : ''
                  }`}
                >
                  {format(day, 'd')}
                </span>
                {dayMeta?.is_working_day && inMonth && (
                  <span className="text-[10px] text-emerald-600 font-medium">{availableSlots} libres</span>
                )}
              </div>

              {inMonth && dayEvents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {dayEvents.slice(0, 2).map((event) => (
                    <div
                      key={event.id}
                      className={`text-[10px] px-1.5 py-0.5 rounded truncate ${
                        event.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                      title={`${event.time} — ${event.subject}`}
                    >
                      {event.time} {event.subject}
                    </div>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="text-[10px] text-gray-500">+{dayEvents.length - 2} autre(s)</div>
                  )}
                </div>
              )}

              {inMonth && dayMeta && !dayMeta.is_working_day && (
                <div className="mt-2 text-[10px] text-gray-400">Fermé</div>
              )}

              {inMonth && dayMeta?.is_working_day && dayEvents.length === 0 && (
                <div className="mt-2 flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-300" title="Créneaux disponibles" />
                </div>
              )}

              {inMonth && hasConfirmed && (
                <span className="sr-only">Rendez-vous confirmés</span>
              )}
              {inMonth && hasPending && (
                <span className="sr-only">Demandes en attente</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Disponible</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300" /> En attente</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300" /> Confirmé</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-300" /> Fermé</span>
      </div>
    </div>
  );
};

export default AppointmentCalendar;
